import { IPlayerAdapter, PlayerInfo, VideoMetadataType, PlatformType } from '@/services/conviva/conviva.types';
import { ConvivaAnalytics } from '@bitmovin/player-integration-conviva';
import Conviva from '@convivainc/conviva-js-coresdk';
import { deviceDetector } from '@/services/conviva/device-detector';
import { detectPlatform } from '@/utils/device';
import packageJson from '../../../../package.json';

/**
 * Get platform-specific player name for Conviva reporting
 * @returns Platform-specific player name (e.g., 'Thmanyah_Tizen', 'Thmanyah_WebOS', 'Thmanyah_Vidaa')
 */
function getPlatformPlayerName(): string {
  const platform = deviceDetector.deviceMetadata.platform;
  switch (platform) {
    case PlatformType.Tizen:
      return 'Thmanyah_Tizen';
    case PlatformType.WebOS:
      return 'Thmanyah_WebOS';
    case PlatformType.Vidaa:
      return 'Thmanyah_Vidaa';
    case PlatformType.PS5:
      return 'Thmanyah_PS5';
    case PlatformType.Web:
    default:
      return 'Thmanyah_TV_WEB_EMULATOR'; // Fallback for web or unknown platforms
  }
}

export class BitmovinAdapter implements IPlayerAdapter {
  player = null;
  videoAnalytics: ConvivaAnalytics | null = null;
  private _cleanupCallbacks: Array<() => void> = [];

  // Time-weighted average bitrate accumulators
  private _avgBrWeightedSum = 0;
  private _avgBrTotalMs = 0;
  private _avgBrIntervalKbps = 0;
  private _avgBrIntervalTs = 0;

  private _resetAvgBitrate() {
    this._avgBrWeightedSum = 0;
    this._avgBrTotalMs = 0;
    this._avgBrIntervalKbps = 0;
    this._avgBrIntervalTs = 0;
  }

  private _reportBitrate(kbps: number) {
    if (kbps <= 0) return;
    const nowMs = Date.now();
    if (this._avgBrIntervalTs > 0 && this._avgBrIntervalKbps > 0) {
      const elapsed = nowMs - this._avgBrIntervalTs;
      this._avgBrWeightedSum += this._avgBrIntervalKbps * elapsed;
      this._avgBrTotalMs += elapsed;
    }
    this._avgBrIntervalKbps = kbps;
    this._avgBrIntervalTs = nowMs;

    const avgKbps = this._avgBrTotalMs > 0 ? Math.round(this._avgBrWeightedSum / this._avgBrTotalMs) : kbps;
    if (avgKbps > 0) {
      try {
        const va = (this.videoAnalytics as any)?.convivaVideoAnalytics;
        if (va) {
          va.reportPlaybackMetric(Conviva.Constants.Playback.AVG_BITRATE, avgKbps);
        }
      } catch {
        // ignore
      }
    }
  }

  async createSession({ assetName = 'NA', viewerId }: { assetName?: string; viewerId?: string }) {
    await deviceDetector.ready;
    // In non-production browser (non-TV), skip Conviva only when using test gateway to avoid TLS issues.
    // When using prod Conviva (no gateway URL), run on localhost so prod behavior can be verified.
    if (process.env.NODE_ENV !== 'production') {
      const platform = typeof window !== 'undefined' ? detectPlatform() : 'web';
      const isTvPlatform = ['webos', 'tizen', 'vidaa', 'ps5'].includes(platform);
      const usingTestGateway = !!(process.env.VUE_APP_CONVIVA_GATEWAY_URL || '').trim();
      if (!isTvPlatform && usingTestGateway) {
        return;
      }
    }

    // Ensure assetName is never empty or 'NA' - use fallback instead of throwing
    const trimmedName = assetName?.trim() || '';
    let validAssetName = trimmedName;
    if (!trimmedName || trimmedName.toUpperCase() === 'NA') {
      const fallbackName = 'Unknown Asset';
      console.warn(`[conviva][BitmovinAdapter] Invalid assetName: "${assetName}". Using fallback: "${fallbackName}".`);
      validAssetName = fallbackName;
    }

    try {
      this.videoAnalytics = new ConvivaAnalytics(undefined, process.env.VUE_APP_CONVIVA_CUSTOMER_KEY || '', {
        gatewayUrl: process.env.VUE_APP_CONVIVA_GATEWAY_URL || '',
        deviceMetadata: {
          category:
            deviceDetector.deviceMetadata.platform === PlatformType.Web
              ? Conviva.Constants.DeviceCategory.WEB
              : deviceDetector.deviceMetadata.platform === PlatformType.PS5
                ? Conviva.Constants.DeviceCategory.PLAYSTATION
                : Conviva.Constants.DeviceCategory.SMART_TV,
          brand: deviceDetector.deviceMetadata.brand,
          manufacturer: deviceDetector.deviceMetadata.manufacturer,
          model: deviceDetector.deviceMetadata.deviceModel,
          type: Conviva.Constants.DeviceType.SMARTTV,
          osName: deviceDetector.deviceMetadata.osName,
          osVersion: deviceDetector.deviceMetadata.osVersion,
        },
      });

      Conviva.Analytics.reportDeviceMetric(
        Conviva.Constants.Network.CONNECTION_TYPE,
        deviceDetector.deviceMetadata.connectionType || 'WiFi',
      );

      // Set assetName, viewerId, and player name before first heartbeat so WSG does not send placeholders
      const validViewerId = viewerId && viewerId.trim() !== '' ? viewerId.trim() : 'NA';
      this.videoAnalytics.updateContentMetadata({
        assetName: validAssetName,
        viewerId: validViewerId,
        additionalStandardTags: {
          [Conviva.Constants.APPLICATION_VERSION]: packageJson.version,
          [Conviva.Constants.PLAYER_NAME]: getPlatformPlayerName(),
        },
      });

      this.videoAnalytics.initializeSession();
    } catch (error) {
      // Never let Conviva issues block Bitmovin playback
      // Optional: console.warn('[conviva][bitmovin] Failed to initialize Conviva session:', error);
      this.videoAnalytics = null;
    }
  }

  destroySession() {
    if (!this.videoAnalytics) {
      return;
    }
    this._resetAvgBitrate();
    this._cleanupCallbacks.forEach(cb => cb());
    this._cleanupCallbacks = [];
    this.videoAnalytics.release();
  }

  setPlayer(player: any) {
    if (!this.videoAnalytics) {
      return;
    }
    this.player = player;

    // CONVIVA BITMOVIN SDK
    this.videoAnalytics.attachPlayer(player);

    const bitsToKbps = (bps: number) => Math.max(0, Math.round(bps / 1000));

    const onQualityChanged = (event: any) => {
      const kbps = bitsToKbps(event?.targetQuality?.bitrate ?? event?.quality?.bitrate ?? 0);
      if (kbps > 0) this._reportBitrate(kbps);
    };

    // Framerate sampling every 2s via getVideoPlaybackQuality()
    let _lastFrameCount = 0;
    let _lastFrameTime = 0;
    const framerateTimer = setInterval(() => {
      const videoEl: HTMLVideoElement | null =
        typeof player.getVideoElement === 'function' ? player.getVideoElement() : null;
      if (!videoEl || typeof videoEl.getVideoPlaybackQuality !== 'function') return;
      const quality = videoEl.getVideoPlaybackQuality();
      const now = performance.now();
      if (_lastFrameTime > 0 && quality.totalVideoFrames > _lastFrameCount) {
        const elapsed = (now - _lastFrameTime) / 1000;
        const fps = Math.round((quality.totalVideoFrames - _lastFrameCount) / elapsed);
        if (fps > 0) {
          try {
            const videoAnalytics = (this.videoAnalytics as any)?.convivaVideoAnalytics;
            if (videoAnalytics) {
              videoAnalytics.reportPlaybackMetric(Conviva.Constants.Playback.RENDERED_FRAMERATE, fps);
            }
          } catch {
            // ignore
          }
        }
      }
      _lastFrameCount = quality.totalVideoFrames;
      _lastFrameTime = now;
    }, 2000);

    if (typeof player.on === 'function') {
      player.on('videoqualitychanged', onQualityChanged);
    }

    this._cleanupCallbacks.push(() => {
      clearInterval(framerateTimer);
      if (this.player && typeof (this.player as any).off === 'function') {
        try {
          (this.player as any).off('videoqualitychanged', onQualityChanged);
        } catch {
          // ignore
        }
      }
    });
  }

  unsetPlayer() {
    this.player = null;
  }

  reportAppBackgrounded(): void {
    try {
      Conviva.Analytics.reportAppBackgrounded();
    } catch {
      // ignore
    }
  }

  reportAppForegrounded(): void {
    try {
      Conviva.Analytics.reportAppForegrounded();
    } catch {
      // ignore
    }
  }

  sendCustomEvent(eventName: string, attributes: Record<string, string>): void {
    if (!this.videoAnalytics) {
      return;
    }
    try {
      this.videoAnalytics.sendCustomPlaybackEvent(eventName, attributes);
    } catch (error) {
      console.warn('[conviva][BitmovinAdapter] Failed to send custom event', eventName, error);
    }
  }

  getPlayerInfo(): PlayerInfo {
    return {
      name: 'Bitmovin',
      version: '1.0.0', // is automatically set by the SDK
    };
  }

  setContentInfo(metadata: VideoMetadataType) {
    if (!this.videoAnalytics) {
      return;
    }
    const trimmedTitle = metadata.title?.trim() || '';
    const validAssetName = trimmedTitle && trimmedTitle.toUpperCase() !== 'NA' ? trimmedTitle : 'Unknown Asset';
    if (validAssetName === 'Unknown Asset') {
      console.warn(`[conviva][BitmovinAdapter] Invalid title in metadata: "${metadata.title}". Using fallback.`);
    }

    const streamUrl = this.getStreamUrlFromPlayer();
    const additionalTags: any = {
      [Conviva.Constants.APPLICATION_VERSION]: packageJson.version,
      [Conviva.Constants.PLAYER_NAME]: getPlatformPlayerName(),
    };
    if (streamUrl) {
      additionalTags[Conviva.Constants.STREAM_URL] = streamUrl;
    }
    this.videoAnalytics.updateContentMetadata({
      streamType: metadata.isLive ? 'LIVE' : 'VOD',
      assetName: validAssetName,
      viewerId: metadata.viewerId || 'NA',
      additionalStandardTags: additionalTags,
      custom: metadata.custom,
    });
  }

  /**
   * Get stream URL from Bitmovin player source
   * @returns Stream URL string or empty string if not available
   */
  private getStreamUrlFromPlayer(): string {
    if (!this.player) {
      return '';
    }
    try {
      // Try to get source from player
      if (typeof (this.player as any).getSource === 'function') {
        const source = (this.player as any).getSource();
        if (source) {
          // Bitmovin source can have hls, dash, or other properties
          return source.hls || source.dash || source.url || '';
        }
      }
      // Fallback: try to get from config
      if (typeof (this.player as any).getConfig === 'function') {
        const config = (this.player as any).getConfig();
        if (config?.source) {
          return config.source.hls || config.source.dash || config.source.url || '';
        }
      }
    } catch (error) {
      console.warn('[conviva][BitmovinAdapter] Failed to get stream URL from player:', error);
    }
    return '';
  }
}
